import { apiGet, apiPost, apiPatch, apiDelete } from './client';

/**
 * MKS-344 — a reusable sales contact: a person, optionally at a company, with
 * an optional e-mail and phone. Nothing else by design.
 */
export interface Contact {
  id: number;
  name: string;
  customerId: number | null;
  customer: { id: number; name: string | null } | null;
  email: string | null;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
  /** Where this contact is used — the contacts list shows it per row. */
  leads: { id: number; name: string; stage: string; isPrimary: boolean }[];
}

export interface ContactInput {
  name: string;
  customerId?: number | null;
  email?: string | null;
  phone?: string | null;
}

export interface ContactListParams {
  /** Matches name, e-mail or company name. */
  search?: string;
  customerId?: number | null;
}

export async function fetchContacts(params: ContactListParams = {}): Promise<{ data: Contact[] }> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.customerId != null) query.set('customerId', String(params.customerId));
  const qs = query.toString();
  return apiGet(`/contacts${qs ? `?${qs}` : ''}`);
}

export async function fetchContact(id: number): Promise<{ data: Contact }> {
  return apiGet(`/contacts/${id}`);
}

export async function createContact(input: ContactInput): Promise<{ data: Contact }> {
  return apiPost('/contacts', input);
}

export async function updateContact(
  id: number,
  input: Partial<ContactInput>,
): Promise<{ data: Contact }> {
  return apiPatch(`/contacts/${id}`, input);
}

/**
 * Soft delete: the contact leaves every picker and this list, but stays on the
 * leads that already reference it. `leadCount` is how many those are, so the
 * UI can say so instead of implying the links are gone.
 */
export async function deleteContact(
  id: number,
): Promise<{ data: { success: boolean; leadCount: number } }> {
  return apiDelete(`/contacts/${id}`);
}
